import { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { UsageCard } from "@/components/UsageMeter";
import { LangToggle } from "@/components/LangToggle";
import { Button } from "@/components/ui/button";
import { Crown, LogOut, FileText, Globe, Trash2 } from "lucide-react";

export default function Settings() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const ctx = useOutletContext();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);

  useEffect(() => { api.usage().then((r) => setUsage(r.data)).catch(() => {}); }, []);

  return (
    <div className="max-w-3xl" data-testid="settings-page">
      <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight mb-6">{t("settings.title")}</h1>

      <div className="card-base p-5 mb-5">
        <h3 className="font-display font-semibold mb-4">{t("settings.account")}</h3>
        <div className="flex items-center gap-4">
          {user?.picture ? <img src={user.picture} alt="" className="h-14 w-14 rounded-full border border-white/10" /> :
            <div className="h-14 w-14 rounded-full bg-cyan-400/15 border border-cyan-400/25 flex items-center justify-center text-cyan-200 text-xl font-semibold">{(user?.name || "U").slice(0,1)}</div>}
          <div className="min-w-0">
            <p className="font-medium">{user?.name}</p>
            <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
            <span className={`inline-flex items-center gap-1 mt-1 text-xs rounded-full px-2 py-0.5 ${user?.is_premium ? "bg-cyan-400/10 text-cyan-200 border border-cyan-400/25" : "bg-white/5 text-muted-foreground border border-white/10"}`}>
              {user?.is_premium && <Crown style={{ width: 12, height: 12 }} />} {user?.is_premium ? t("common.premium") : t("common.freePlan")}
            </span>
          </div>
        </div>
      </div>

      <div className="card-base p-5 mb-5">
        <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><Globe style={{ width: 18, height: 18 }} /> {t("settings.language")}</h3>
        <LangToggle showIcon />
      </div>

      <div className="mb-5"><UsageCard usage={usage} onUpgrade={ctx?.openUpgrade} /></div>

      {!user?.is_premium && (
        <div className="card-base p-5 mb-5 border-cyan-400/20">
          <h3 className="font-display font-semibold flex items-center gap-2"><Crown className="text-cyan-300" style={{ width: 18, height: 18 }} /> {t("settings.goPremium")}</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-3">{t("settings.goPremiumDesc")}</p>
          <Button data-testid="settings-upgrade-button" onClick={ctx?.openUpgrade} className="bg-cyan-400 text-slate-900 hover:bg-cyan-300 font-semibold">{t("settings.upgradeNow")}</Button>
        </div>
      )}

      <div className="card-base p-5 mb-5">
        <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><FileText style={{ width: 18, height: 18 }} /> {t("settings.legal")}</h3>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate("/privacy")} className="border-white/15 bg-white/5 hover:bg-white/10">{t("settings.privacy")}</Button>
          <Button variant="outline" onClick={() => navigate("/terms")} className="border-white/15 bg-white/5 hover:bg-white/10">{t("settings.terms")}</Button>
          <Button variant="outline" onClick={() => navigate("/ai-notice")} className="border-white/15 bg-white/5 hover:bg-white/10">{t("settings.aiNotice")}</Button>
        </div>
        <div className="mt-3 pt-3 border-t border-white/10">
          <Button data-testid="settings-delete-account-button" variant="outline" onClick={() => navigate("/delete-account")} className="border-rose-400/30 text-rose-200 hover:bg-rose-500/10">
            <Trash2 style={{ width: 16, height: 16 }} className="mr-2" /> {t("settings.deleteAccount")}
          </Button>
        </div>
      </div>

      <Button data-testid="settings-logout-button" onClick={logout} variant="outline" className="border-rose-400/30 text-rose-200 hover:bg-rose-500/10">
        <LogOut style={{ width: 16, height: 16 }} className="mr-2" /> {t("common.signOut")}
      </Button>
    </div>
  );
}
